import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

interface ContextMenuProps {
  contextMenu: { x: number; y: number } | null;
  isBestFit: boolean;
  menuRef: React.RefObject<HTMLDivElement | null>;
  onToggle: (e: React.MouseEvent) => void;
}

function BestFitContextMenuUI({ contextMenu, isBestFit, menuRef, onToggle }: ContextMenuProps) {
  if (!contextMenu) return null;
  return createPortal(
    <div
      ref={menuRef}
      className="fixed bg-gray-800 border border-gray-600 rounded shadow-xl py-1 z-[99999] text-[13px] text-gray-200 select-none"
      style={{ top: contextMenu.y, left: contextMenu.x, minWidth: '160px' }}
    >
      <div
        className="px-4 py-1.5 hover:bg-gray-700 cursor-pointer whitespace-nowrap flex items-center justify-between gap-4"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={onToggle}
      >
        <span>Best Fit Column</span>
        {isBestFit && <span className="text-blue-400 text-[11px] font-bold">✓</span>}
      </div>
    </div>,
    document.body
  );
}

export function useBestFitContextMenu() {
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [isBestFit, setIsBestFit] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Clamp to viewport so menu never goes off-screen
    const menuW = 180;
    const menuH = 40;
    const x = Math.min(e.clientX, window.innerWidth  - menuW - 8);
    const y = Math.min(e.clientY, window.innerHeight - menuH - 8);
    setContextMenu({ x, y });
  }, []);

  const closeContextMenu = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Close on mousedown outside the menu
  useEffect(() => {
    if (!contextMenu) return;

    const onMouseDown = (e: MouseEvent) => {
      if (menuRef.current && menuRef.current.contains(e.target as Node)) return;
      closeContextMenu();
    };

    // Delay so the right-click that opened the menu doesn't immediately close it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', onMouseDown);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [contextMenu, closeContextMenu]);

  // Close on Escape key
  useEffect(() => {
    if (!contextMenu) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeContextMenu(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [contextMenu, closeContextMenu]);

  const toggleBestFit = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsBestFit(prev => !prev);
    closeContextMenu();
  }, [closeContextMenu]);

  const ContextMenu = () => (
    <BestFitContextMenuUI
      contextMenu={contextMenu}
      isBestFit={isBestFit}
      menuRef={menuRef}
      onToggle={toggleBestFit}
    />
  );

  return { isBestFit, handleContextMenu, ContextMenu };
}
